import { ChangeEvent, FormEvent } from "react";
import { Button, Input, LoadingProgress, RadioButton } from "@/ui";
import {
    AuthFormError,
    AuthPasswordField,
    AuthSubmitButton,
    AuthTextButton,
    AuthUsernameField,
    formStyles,
} from "./AuthFormShared";

const noop = () => undefined;
const noopSubmit = (event: FormEvent) => {
    event.preventDefault();
};

export const formatCognitoAttributeLabel = (attribute: string) =>
    attribute
        .split("_")
        .map(part => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
        .join(" ");

export type AuthFormViewHandlers = {
    onFieldChange?: (event: ChangeEvent<HTMLInputElement>) => void;
    onSubmit?: (event: FormEvent) => void;
};

export type SignInFormViewProps = AuthFormViewHandlers & {
    error?: string | null;
    loading?: boolean;
    defaultUsername?: string;
    usernameLabel?: string;
    onForgotPassword?: () => void;
};

export function SignInFormView({
    error,
    loading,
    defaultUsername,
    usernameLabel = "Username",
    onFieldChange = noop,
    onSubmit = noopSubmit,
    onForgotPassword = noop,
}: SignInFormViewProps) {
    return (
        <form className={formStyles.form} onSubmit={onSubmit}>
            <AuthFormError message={error} />
            <AuthUsernameField
                autoFocus
                data-test="sign-in-username-input"
                defaultValue={defaultUsername}
                label={usernameLabel}
                name="username"
                onChange={onFieldChange}
            />
            <AuthPasswordField
                data-test="sign-in-password-input"
                label="Password"
                name="password"
                onChange={onFieldChange}
            />
            <div className={formStyles.actions}>
                <span className={formStyles.plainLabel}>
                    Forgot your password?
                </span>
                <AuthTextButton
                    data-test="sign-in-forgot-password-link"
                    onClick={onForgotPassword}
                    tone="green"
                >
                    Reset password
                </AuthTextButton>
            </div>
            <AuthSubmitButton
                data-test="sign-in-sign-in-button"
                loading={loading}
            >
                SIGN IN
            </AuthSubmitButton>
        </form>
    );
}

export type ForgotPasswordFormViewProps = AuthFormViewHandlers & {
    error?: string | null;
    mode: "request" | "confirm";
    usernameLabel?: string;
    onResendCode?: () => void;
    onBackToSignIn?: () => void;
};

export function ForgotPasswordFormView({
    error,
    mode,
    usernameLabel = "Username",
    onFieldChange = noop,
    onSubmit = noopSubmit,
    onResendCode = noop,
    onBackToSignIn = noop,
}: ForgotPasswordFormViewProps) {
    return (
        <form className={formStyles.form} onSubmit={onSubmit}>
            <AuthFormError message={error} />
            {mode === "confirm" ? (
                <>
                    <Input
                        autoFocus
                        className={formStyles.field}
                        fullWidth
                        label="Code"
                        name="code"
                        onChange={onFieldChange}
                    />
                    <AuthPasswordField
                        autoComplete="new-password"
                        label="New Password"
                        name="password"
                        onChange={onFieldChange}
                    />
                </>
            ) : (
                <AuthUsernameField
                    autoFocus
                    label={usernameLabel}
                    name="username"
                    onChange={onFieldChange}
                />
            )}
            <AuthSubmitButton>
                {mode === "confirm" ? "Submit" : "Send Code"}
            </AuthSubmitButton>
            <div className={formStyles.actions}>
                {mode === "confirm" ? (
                    <AuthTextButton onClick={onResendCode} tone="green">
                        Resend Code
                    </AuthTextButton>
                ) : (
                    <AuthTextButton onClick={onBackToSignIn} tone="green">
                        Back to Sign In
                    </AuthTextButton>
                )}
            </div>
        </form>
    );
}

export type ConfirmSignInFormViewProps = AuthFormViewHandlers & {
    error?: string | null;
    mfaType?: "SMS" | "TOTP";
    onBackToSignIn?: () => void;
};

export function ConfirmSignInFormView({
    error,
    mfaType = "SMS",
    onFieldChange = noop,
    onSubmit = noopSubmit,
    onBackToSignIn = noop,
}: ConfirmSignInFormViewProps) {
    return (
        <form className={formStyles.form} onSubmit={onSubmit}>
            <AuthFormError message={error} />
            <Input
                autoFocus
                className={formStyles.field}
                fullWidth
                label="Code"
                name="code"
                onChange={onFieldChange}
            />
            <AuthSubmitButton>Confirm</AuthSubmitButton>
            <div className={formStyles.actions}>
                <AuthTextButton onClick={onBackToSignIn} tone="green">
                    Back to Sign In
                </AuthTextButton>
            </div>
        </form>
    );
}

export type RequireNewPasswordFormViewProps = AuthFormViewHandlers & {
    error?: string | null;
    requiredAttributes?: string[];
    onBackToSignIn?: () => void;
};

export function RequireNewPasswordFormView({
    error,
    requiredAttributes = [],
    onFieldChange = noop,
    onSubmit = noopSubmit,
    onBackToSignIn = noop,
}: RequireNewPasswordFormViewProps) {
    return (
        <form className={formStyles.form} onSubmit={onSubmit}>
            <AuthFormError message={error} />
            <AuthPasswordField
                autoComplete="new-password"
                autoFocus
                label="New Password"
                name="password"
                onChange={onFieldChange}
            />
            {requiredAttributes.map(attribute => (
                <Input
                    className={formStyles.field}
                    fullWidth
                    key={attribute}
                    label={formatCognitoAttributeLabel(attribute)}
                    name={attribute}
                    onChange={onFieldChange}
                    type="text"
                />
            ))}
            <AuthSubmitButton>Change</AuthSubmitButton>
            <div className={formStyles.actions}>
                <AuthTextButton onClick={onBackToSignIn} tone="green">
                    Back to Sign In
                </AuthTextButton>
            </div>
        </form>
    );
}

export type VerifyContactFormViewProps = AuthFormViewHandlers & {
    error?: string | null;
    mode: "select" | "code";
    showEmail?: boolean;
    showPhone?: boolean;
    onSkip?: () => void;
};

export function VerifyContactFormView({
    error,
    mode,
    showEmail = true,
    showPhone = true,
    onFieldChange = noop,
    onSubmit = noopSubmit,
    onSkip = noop,
}: VerifyContactFormViewProps) {
    return (
        <form className={formStyles.form} onSubmit={onSubmit}>
            <AuthFormError message={error} />
            {mode === "code" ? (
                <Input
                    autoFocus
                    className={formStyles.field}
                    fullWidth
                    label="Code"
                    name="code"
                    onChange={onFieldChange}
                />
            ) : (
                <div className={formStyles.radioGroup}>
                    {showEmail ? (
                        <RadioButton
                            label="Email"
                            name="contact"
                            onChange={onFieldChange}
                            value="email"
                        />
                    ) : null}
                    {showPhone ? (
                        <RadioButton
                            label="Phone Number"
                            name="contact"
                            onChange={onFieldChange}
                            value="phone_number"
                        />
                    ) : null}
                </div>
            )}
            <AuthSubmitButton>
                {mode === "code" ? "Submit" : "Verify"}
            </AuthSubmitButton>
            <div className={formStyles.actions}>
                <AuthTextButton onClick={onSkip} tone="green">
                    Skip
                </AuthTextButton>
            </div>
        </form>
    );
}

export type ConfirmSignUpFormViewProps = AuthFormViewHandlers & {
    error?: string | null;
    username?: string;
    usernameLabel?: string;
    usernameDisabled?: boolean;
    onResendCode?: () => void;
    onBackToSignIn?: () => void;
};

export function ConfirmSignUpFormView({
    error,
    username,
    usernameLabel = "Username",
    usernameDisabled,
    onFieldChange = noop,
    onSubmit = noopSubmit,
    onResendCode = noop,
    onBackToSignIn = noop,
}: ConfirmSignUpFormViewProps) {
    return (
        <form className={formStyles.form} onSubmit={onSubmit}>
            <AuthFormError message={error} />
            <AuthUsernameField
                defaultValue={username}
                disabled={usernameDisabled}
                label={usernameLabel}
                name="username"
                onChange={onFieldChange}
            />
            <Input
                autoFocus
                className={formStyles.field}
                fullWidth
                label="Confirmation Code"
                name="code"
                onChange={onFieldChange}
            />
            <p className={formStyles.hint}>
                Lost your code?{" "}
                <AuthTextButton onClick={onResendCode} tone="green">
                    Resend Code
                </AuthTextButton>
            </p>
            <AuthSubmitButton>Confirm</AuthSubmitButton>
            <div className={formStyles.actions}>
                <AuthTextButton onClick={onBackToSignIn} tone="green">
                    Back to Sign In
                </AuthTextButton>
            </div>
        </form>
    );
}

export function AuthLoadingFormView() {
    return (
        <LoadingProgress indeterminate indicator="linear" title="Loading…" />
    );
}

export type TOTPSetupFormViewProps = AuthFormViewHandlers & {
    error?: string | null;
    secretCode?: string;
};

/** Storybook / visual reference — production uses Amplify `TOTPSetupComp`. */
export function TOTPSetupFormView({
    error,
    secretCode = "ABCD EFGH IJKL MNOP",
    onFieldChange = noop,
    onSubmit = noopSubmit,
}: TOTPSetupFormViewProps) {
    return (
        <form className={formStyles.form} onSubmit={onSubmit}>
            <AuthFormError message={error} />
            <p className={formStyles.hint}>
                Scan the QR code with your authenticator app, then enter the
                verification code below.
            </p>
            <div
                aria-hidden
                style={{
                    alignSelf: "center",
                    background: "#fff",
                    border: "1px solid var(--ds-color-border-default, #e5e7eb)",
                    borderRadius: 8,
                    height: 160,
                    margin: "0.5rem 0 1rem",
                    width: 160,
                }}
            />
            <p className={formStyles.hint}>
                Secret code: <strong>{secretCode}</strong>
            </p>
            <Input
                autoFocus
                className={formStyles.field}
                fullWidth
                label="Verification code"
                name="code"
                onChange={onFieldChange}
            />
            <AuthSubmitButton>Verify</AuthSubmitButton>
        </form>
    );
}

export type AccessNotAvailableFormViewProps = {
    onBackToDashboard?: () => void;
};

export function AccessNotAvailableFormView({
    onBackToDashboard = noop,
}: AccessNotAvailableFormViewProps) {
    return (
        <>
            <p className={formStyles.accessMessage}>
                Please contact the support team for assistance.
            </p>
            <Button
                className={formStyles.submit}
                fullWidth
                onClick={onBackToDashboard}
                size="lg"
                type="button"
                variant="primary"
            >
                Back to Treez Dashboard
            </Button>
        </>
    );
}
